/**
 * Staff — Account Management Board.
 *
 * Kanban for prospects who want to buy Ox products directly (installers
 * currently sourcing elsewhere who are considering a switch). Separate from
 * Manage Leads — leads = end-customers looking to be paired with an
 * installer; accounts = new installers we're trying to onboard.
 *
 * Pipeline (drag-only, no auto-advance):
 *   New Enquiry → Spoken To → First Project Quoted → First Order Placed → Active Customer
 *
 * PATCH /api/staff/accounts/:id { status: <target-key> } moves a card.
 * Admins & managers see a trash icon on each card (soft-delete via DELETE).
 * Team members can drive the Kanban but not create/delete.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, Users, Plus, Trash2, Search, X, Phone, Mail,
  UserPlus, MessageCircle, FileText, ShoppingCart, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';
import NewAccountModal from '@/components/accounts/NewAccountModal';
import { KanbanColumn, MoveToDropdown, KANBAN_PALETTE } from '@/components/kanban/KanbanShared';
import logger from '@/utils/logger';

const COLUMNS = [
  { key: 'new_enquiry',          label: 'New Enquiry',          colour: KANBAN_PALETTE.slate,   icon: UserPlus },
  { key: 'spoken_to',            label: 'Spoken To',            colour: KANBAN_PALETTE.blue,    icon: MessageCircle },
  { key: 'first_project_quoted', label: 'First Project Quoted', colour: KANBAN_PALETTE.amber,   icon: FileText },
  { key: 'first_order_placed',   label: 'First Order Placed',   colour: KANBAN_PALETTE.green,   icon: ShoppingCart },
  { key: 'active_customer',      label: 'Active Customer',      colour: KANBAN_PALETTE.indigo,  icon: Star },
];

const emptyByCol = () => Object.fromEntries(COLUMNS.map((c) => [c.key, []]));

const fmtAge = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = ms / (1000 * 60 * 60);
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

export default function AccountManagementBoard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canAdmin = ['admin', 'manager'].includes(user?.role);
  const homePath = useHomePath();
  const [byCol, setByCol] = useState(emptyByCol());
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  // Owner filter — 'all' (default), 'me', or a specific teammate's user_id.
  // Persisted per-user so a refresh keeps the current view.
  const [ownerScope, setOwnerScope] = useState(() => localStorage.getItem('acc-board-owner') || 'all');
  const [teammates, setTeammates] = useState([]);
  useEffect(() => { localStorage.setItem('acc-board-owner', ownerScope); }, [ownerScope]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/support/staff-members`, { credentials: 'include' });
        if (res.ok) setTeammates(await res.json());
      } catch (e) { logger.warn('[accounts] teammate roster load failed', e); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load');
      const rows = await res.json();
      const grouped = emptyByCol();
      rows.forEach((a) => {
        if (grouped[a.status]) grouped[a.status].push(a);
      });
      setByCol(grouped);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [user, navigate, load]);

  const findAccount = useCallback((id) => {
    for (const c of COLUMNS) {
      const hit = (byCol[c.key] || []).find((a) => a.id === id);
      if (hit) return hit;
    }
    return null;
  }, [byCol]);

  const move = async (id, target) => {
    const src = findAccount(id);
    if (!src || src.status === target) return;
    // optimistic update
    setByCol((prev) => {
      const next = emptyByCol();
      for (const key of Object.keys(prev)) next[key] = prev[key].filter((a) => a.id !== id);
      next[target] = [{ ...src, status: target, updated_at: new Date().toISOString() }, ...next[target]];
      return next;
    });
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: target }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Move failed');
      const label = COLUMNS.find((c) => c.key === target)?.label;
      toast.success(`${src.reference} → ${label}`);
    } catch (err) {
      toast.error(err.message);
      load(); // roll back to server state
    }
  };

  const softDelete = async (account, e) => {
    e?.stopPropagation?.();
    if (!canAdmin) return;
    const ref = account.reference;
    const originalStatus = account.status;
    // optimistic removal
    setByCol((prev) => {
      const next = { ...prev };
      next[originalStatus] = (prev[originalStatus] || []).filter((a) => a.id !== account.id);
      return next;
    });
    try {
      const res = await fetch(`${API_URL}/api/staff/accounts/${account.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast(`${ref} deleted`, {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const r = await fetch(`${API_URL}/api/staff/accounts/${account.id}/restore`, {
                method: 'POST', credentials: 'include',
              });
              if (!r.ok) throw new Error('Restore failed');
              toast.success(`${ref} restored`);
              load();
            } catch (err) { toast.error(err.message); }
          },
        },
      });
    } catch (err) {
      toast.error(err.message);
      load();
    }
  };

  const matches = useCallback((a, q) => {
    // Owner scope: 'all' passes everything, 'me' matches only cards owned
    // by the current user, otherwise match the specific teammate user_id.
    if (ownerScope === 'me' && a.assigned_to !== user?.user_id) return false;
    if (ownerScope !== 'all' && ownerScope !== 'me' && a.assigned_to !== ownerScope) return false;
    if (!q) return true;
    const bag = [a.name, a.reference, a.phone, a.email, a.current_supplier, a.interested_in, a.notes]
      .filter(Boolean).join(' ').toLowerCase();
    return bag.includes(q);
  }, [ownerScope, user?.user_id]);

  const columns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return COLUMNS.map((c) => {
      const rows = (byCol[c.key] || []).filter((a) => matches(a, q));
      return {
        ...c,
        rows: [...rows].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')),
      };
    });
  }, [byCol, search, matches]);

  const totalFiltered = columns.reduce((n, c) => n + c.rows.length, 0);
  const totalAll = Object.values(byCol).reduce((n, arr) => n + arr.length, 0);

  const moveOptions = COLUMNS.map((c) => ({ key: c.key, label: c.label }));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white sticky top-0 z-20" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-[1600px] mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/staff')}
            data-testid="account-board-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Users className="w-5 h-5 shrink-0" /> Account Management
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              {totalAll} in pipeline{search.trim() ? ` · ${totalFiltered} matches` : ''} · Use "Move to" on a card to advance stages
            </p>
          </div>
          <select
            value={ownerScope}
            onChange={(e) => setOwnerScope(e.target.value)}
            data-testid="account-owner-scope"
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs font-semibold text-white focus:outline-none focus:bg-white/20 focus:border-white/40 cursor-pointer max-w-[200px] shrink-0"
            aria-label="Filter prospects by owner"
          >
            <option value="all" className="text-slate-900">All owners</option>
            <option value="me" className="text-slate-900">Assigned to me</option>
            {teammates.length > 0 && (
              <optgroup label="Assigned to teammate">
                {teammates.filter((t) => t.user_id !== user?.user_id).map((t) => (
                  <option key={t.user_id} value={t.user_id} className="text-slate-900">{t.name}</option>
                ))}
              </optgroup>
            )}
          </select>
          <div className="relative shrink-0">
            <Search className="w-4 h-4 text-white/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, email…"
              data-testid="account-board-search"
              className="pl-9 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-sm placeholder-white/50 text-white w-56 md:w-72 focus:outline-none focus:bg-white/20 focus:border-white/40"
              aria-label="Search accounts"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {canAdmin && (
            <Button
              onClick={() => setShowNew(true)}
              size="sm"
              className="bg-white text-slate-900 hover:bg-slate-100 shrink-0"
              data-testid="account-board-new"
            >
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New Enquiry</span>
            </Button>
          )}
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading pipeline…</Card>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
            {columns.map((col) => (
              <KanbanColumn
                key={col.key}
                column={col}
                count={col.rows.length}
                testId={`account-col-${col.key}`}
                emptyLabel="No prospects"
              >
                {col.rows.map((a) => (
                  <Card
                    key={a.id}
                    onClick={() => navigate(`/staff/accounts/${a.id}`)}
                    className="group p-3 cursor-pointer hover:shadow-md transition border bg-white"
                    data-testid={`account-card-${a.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[11px] text-gray-500 truncate flex-1 min-w-0">{a.reference}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">{fmtAge(a.updated_at)}</span>
                      {canAdmin && (
                        <button
                          type="button"
                          onClick={(e) => softDelete(a, e)}
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded p-1 -mr-1 -my-1 transition"
                          data-testid={`account-delete-${a.id}`}
                          title="Delete enquiry"
                          aria-label="Delete enquiry"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="font-semibold text-sm text-gray-900 truncate" title={a.name}>{a.name}</div>
                    {(a.phone || a.email) && (
                      <div className="mt-1 flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                        {a.phone && (
                          <a href={`tel:${a.phone}`} className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 truncate">
                            <Phone className="w-3 h-3 shrink-0" /> {a.phone}
                          </a>
                        )}
                        {a.email && (
                          <a href={`mailto:${a.email}`} className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{a.email}</span>
                          </a>
                        )}
                      </div>
                    )}
                    {a.current_supplier && (
                      <div className="text-[11px] text-slate-500 mt-1 truncate">
                        <span className="font-semibold text-slate-600">Uses:</span> {a.current_supplier}
                      </div>
                    )}
                    {a.interested_in && (
                      <div className="text-[11px] text-slate-500 truncate">
                        <span className="font-semibold text-slate-600">Wants:</span> {a.interested_in}
                      </div>
                    )}
                    <div className="mt-2">
                      <MoveToDropdown
                        current={a.status}
                        options={moveOptions}
                        onChange={(target) => move(a.id, target)}
                        testId={`account-move-${a.id}`}
                      />
                    </div>
                    {a.assigned_to ? (
                      <div className="text-[10px] text-indigo-700 mt-1 inline-flex items-center gap-1 font-semibold">
                        <span className="inline-block w-1.5 h-1.5 bg-indigo-600 rounded-full" />
                        {teammates.find((t) => t.user_id === a.assigned_to)?.name || 'Assigned'}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await fetch(`${API_URL}/api/staff/accounts/${a.id}/assign-me`, {
                              method: 'POST', credentials: 'include',
                            });
                            if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed');
                            const fresh = await res.json();
                            setByCol((prev) => {
                              const next = { ...prev };
                              next[fresh.status] = (prev[fresh.status] || []).map((r) => r.id === fresh.id ? fresh : r);
                              return next;
                            });
                            toast.success(`Assigned ${fresh.reference} to you`);
                          } catch (err) { toast.error(err.message); }
                        }}
                        className="text-[10px] text-slate-500 hover:text-indigo-700 mt-1 font-semibold underline underline-offset-2 decoration-dotted"
                        data-testid={`account-assign-me-${a.id}`}
                      >
                        Assign to me
                      </button>
                    )}
                  </Card>
                ))}
              </KanbanColumn>
            ))}
            </div>
          </div>
        )}
      </div>

      {showNew && (
        <NewAccountModal
          onClose={() => setShowNew(false)}
          onCreated={(acc) => {
            setByCol((prev) => ({ ...prev, [acc.status]: [acc, ...(prev[acc.status] || [])] }));
            setShowNew(false);
          }}
          onImported={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}
